'use client'

import React from 'react'
import {
	Folder,
	ChevronDown,
	Home,
	ShoppingCart,
	Package,
	Users2,
	Calculator,
	BookAlert,
	Database,
	ArrowLeft
} from 'lucide-react'
import Link from 'next/link'
import { useState, useEffect, useRef } from 'react'
import { cn } from '@/libs/utils'
import { LogoIcon } from '../icons/common/logo'
import { redirect, usePathname } from 'next/navigation'
import { getRouters } from '@/apis/account'
import { getNdlist } from '@/apis/transactional'
import { redirectToAuthLogin } from '@/utils/auth'

// 创建图标映射对象，将字符串名称映射到实际的图标组件
const iconMap: Record<string, React.ComponentType<any>> = {
	Home,
	ShoppingCart,
	Package,
	Users2,
	Calculator,
	Folder,
	BookAlert,
	Database
}
type MenuState = 'full' | 'collapsed' | 'hidden'

interface SubMenuItem {
	id: string
	label: string
	href: string
	icon?: React.ComponentType<any>
	badge?: string
	isNew?: boolean
	children?: SubMenuItem[]
}

interface MenuItem {
	id: string
	label: string
	href?: string
	icon: React.ComponentType<any>
	badge?: string
	isNew?: boolean
	children?: SubMenuItem[]
}

interface MenuSection {
	id: string
	label: string
	items: MenuItem[]
}

export default function Sidebar() {
	const token = localStorage.getItem('token')
	if (!token) {
		// redirect('/login') // login todo
		redirectToAuthLogin()
	}

	const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
	const [menuState, setMenuState] = useState<MenuState>('full')
	const [isHovered, setIsHovered] = useState(false)
	const [previousDesktopState, setPreviousDesktopState] = useState<MenuState>('full')
	const [isMobile, setIsMobile] = useState(false)
	const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set())
	const [menuData, setMenuData] = useState<MenuSection[]>([])
	const pathname = usePathname()
	const sidebarRef = useRef<HTMLDivElement>(null)
	const isMounted = useRef(true)

	// 检查子菜单是否有选中项，如果有则展开父菜单
	const checkAndExpandActiveParents = () => {
		if (!pathname) {
			return
		}

		const newExpandedItems = new Set(expandedItems)

		// 递归检查每个菜单项及其子项
		const checkItems = (items: (MenuItem | SubMenuItem)[], parentId: string = '') => {
			items?.forEach(item => {
				const itemId = parentId ? `${parentId}-${item.id}` : item.id

				// 检查当前项是否有子项
				if (item.children && item.children.length > 0) {
					// 检查子项是否有选中的
					const hasActiveChild = item.children.some(child => {
						return child.href && (
							pathname === child.href ||
							(child.href !== '/' && pathname.startsWith(child.href))
						)
					})

					// 如果子项有选中的，则展开父项
					if (hasActiveChild) {
						newExpandedItems.add(itemId)
					}

					// 继续检查子项的子项
					checkItems(item.children, itemId)
				}
			})
		}

		// 遍历所有菜单部分
		menuData.forEach(section => {
			checkItems(section.items, section.id)
		})

		// 如果有变化，更新展开项集合
		if (JSON.stringify(Array.from(newExpandedItems)) !== JSON.stringify(Array.from(expandedItems))) {
			setExpandedItems(newExpandedItems)
		}
	}

	// 在组件挂载和路径变化时检查
	useEffect(() => {
		checkAndExpandActiveParents()
	}, [pathname, menuData])

	// Cycle through menu states: full -> collapsed -> hidden -> full
	const toggleMenuState = () => {
		setMenuState((prev) => {
			switch (prev) {
				case 'full':
					return 'collapsed'
				case 'collapsed':
					return 'hidden'
				case 'hidden':
					return 'full'
				default:
					return 'full'
			}
		})
	}

	// Function to set menu state from theme customize
	const setMenuStateFromCustomize = (state: MenuState) => {
		if (!isMobile) {
			setMenuState(state)
		}
	}

	// Handle responsive behavior
	useEffect(() => {
		const handleResize = () => {
			const isDesktop = window.innerWidth >= 1024 // lg breakpoint
			setIsMobile(!isDesktop)

			if (!isDesktop) {
				// On mobile/tablet, save current desktop state and set to hidden
				if (menuState !== 'hidden') {
					setPreviousDesktopState(menuState)
					setMenuState('hidden')
				}
			} else {
				// On desktop, restore previous state if coming from mobile
				if (menuState === 'hidden' && previousDesktopState !== 'hidden') {
					setMenuState(previousDesktopState)
				}
			}
		}

		// Check on mount
		handleResize()

		// Add event listener
		window.addEventListener('resize', handleResize)

		return () => window.removeEventListener('resize', handleResize)
	}, [menuState, previousDesktopState])

	// 递归处理菜单数据，将icon字符串映射到实际的图标组件
	const processMenuData = (data: any[]): MenuSection[] => {
		return data.map(section => ({
			...section,
			items: section.items?.map((item: MenuItem) => ({
				...item,
				// 如果icon是字符串，从映射中获取对应的组件
				icon: typeof item.icon === 'string' ? iconMap[item.icon] || Folder : item.icon,
				// 递归处理子项
				children: item.children ? item.children.map((child: SubMenuItem) => ({
					...child,
					icon: typeof child.icon === 'string' ? iconMap[child.icon] || Folder : child.icon
				})) : undefined
			}))
		}))
	}

	// Fetch menu data from API - 只在登录后请求一次，使用localStorage缓存
	useEffect(() => {
		const fetchMenuData = async () => {
			try {
				// 首先检查localStorage中是否已有缓存的菜单数据
				// const cachedMenuData = localStorage.getItem('menuData')
				// if (cachedMenuData) {
				// 	// 如果有缓存，直接使用
				// 	const parsedMenuData = JSON.parse(cachedMenuData)
				// 	if (parsedMenuData && Array.isArray(parsedMenuData)) {
				// 		const processedMenuData = processMenuData(parsedMenuData)
				// 		if (isMounted.current) {
				// 			setMenuData(processedMenuData)
				// 		}
				// 		return // 有缓存就不再请求API
				// 	}
				// }

				// 没有缓存时才请求API
				// 每次刷新都调用接口
				const response = await getRouters()
				console.info('menuData from API', response)
				if (isMounted.current && response.data && Array.isArray(response.data)) {
					// 处理menuData，将icon字符串转换为实际的图标组件
					const processedMenuData = processMenuData(response.data)
					setMenuData(processedMenuData)
					// 将原始数据（不包含组件引用）缓存到localStorage
					localStorage.setItem('menuData', JSON.stringify(response.data))
				}
			} catch (error) {
				console.error('Failed to fetch menu data:', error)
				// Use empty array as fallback to prevent UI errors
				setMenuData([])
			}
		}

		fetchMenuData()

		return () => {
			isMounted.current = false
		}
	}, [])

	// Export functions to window for TopNav and ThemeCustomize to access
	useEffect(() => {
		if (typeof window !== 'undefined') {
			; (window as any).toggleMenuState = toggleMenuState
				; (window as any).menuState = menuState
				; (window as any).isHovered = isHovered
				; (window as any).isMobile = isMobile
				; (window as any).setIsMobileMenuOpen = setIsMobileMenuOpen
				; (window as any).isMobileMenuOpen = isMobileMenuOpen
				; (window as any).setMenuStateFromCustomize = setMenuStateFromCustomize
		}
	}, [menuState, isHovered, isMobile, isMobileMenuOpen])

	function handleNavigation() {
		if (isMobile) {
			setIsMobileMenuOpen(false)
		}
	}

	const toggleExpanded = (itemId: string) => {
		setExpandedItems((prev) => {
			const newSet = new Set(prev)
			if (newSet.has(itemId)) {
				newSet.delete(itemId)
			} else {
				newSet.add(itemId)
			}
			return newSet
		})
	}

	function NavItem({
		item,
		level = 0,
		parentId = '',
	}: {
		item: MenuItem | SubMenuItem
		level?: number
		parentId?: string
	}) {
		const itemId = `${parentId}-${item.id}`
		const isExpanded = expandedItems.has(itemId)
		const hasChildren = item.children && item.children.length > 0
		const showText = menuState === 'full' || (menuState === 'collapsed' && isHovered) || (isMobile && isMobileMenuOpen)
		const showExpandIcon = hasChildren && showText
		// 检查当前项是否被选中
		const isActive = item.href && pathname && (
			pathname === item.href ||
			(item.href !== '/' && pathname.startsWith(item.href))
		)

		const paddingLeft = level === 0 ? 'px-3' : level === 1 ? 'pl-8 pr-3' : 'pl-12 pr-3'

		const content = (
			<div
				className={cn(
					'flex items-center py-2 text-sm rounded-md transition-colors sidebar-menu-item relative group cursor-pointer',
					paddingLeft,
					// 选中时不加hover样式，非选中时加hover样式
					!isActive && 'hover:bg-gray-100 dark:hover:bg-[#1F1F23]',
					isActive && 'bg-gray-900 dark:bg-gray-800 text-white dark:text-white'
				)}
				onClick={() => {
					if (hasChildren) {
						toggleExpanded(itemId)
					}
					//  else if (item.href) {
					// 	// Navigate to the href
					// 	window.location.href = item.href
					// 	handleNavigation()
					// }
				}}
				title={menuState === 'collapsed' && !isHovered && !isMobile ? item.label : undefined}
			>
				{item.icon && React.createElement(item.icon, { className: cn('h-4 w-4 flex-shrink-0 sidebar-menu-icon', isActive && 'text-white') })}

				{showText && (
					<>
						<span className="ml-3 flex-1 transition-opacity duration-200 sidebar-menu-text">{item.label}</span>

						{/* Badges and indicators */}
						<div className="flex items-center space-x-1">
							{item.isNew && (
								<span className="px-1.5 py-0.5 text-xs bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 rounded-full">
									New
								</span>
							)}
							{item.badge && (
								<span className={cn(
									'px-1.5 py-0.5 text-xs rounded-full',
									isActive
										? 'bg-white text-gray-900 dark:bg-gray-700 dark:text-white'
										: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
								)}
								>
									{item.badge}
								</span>
							)}
							{showExpandIcon && (
								<ChevronDown
									className={cn('h-3 w-3 transition-transform duration-200', isExpanded ? 'rotate-180' : 'rotate-0', isActive && 'text-white')}
								/>
							)}
						</div>
					</>
				)}

				{/* Tooltip for collapsed state when not hovered and not mobile */}
				{menuState === 'collapsed' && !isHovered && !isMobile && (
					<div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900 text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-40">
						{item.label}
						{item.badge && <span className="ml-1 text-blue-300">({item.badge})</span>}
					</div>
				)}
			</div>
		)

		return (
			<div>
				{/* {item.href && !hasChildren ? <Link href={item.href}>{content}</Link> : content} */}
				{item.href ? (
					<Link href={item.href} onClick={handleNavigation}>
						{content}
					</Link>
				) : (
					content
				)}
				{hasChildren && isExpanded && showText && (
					<div className="mt-1 space-y-1">
						{item.children!.map((child) => (
							<NavItem key={child.id} item={child} level={level + 1} parentId={itemId} />
						))}
					</div>
				)}
			</div>
		)
	}

	// Calculate sidebar width - expand when collapsed and hovered, or full width on mobile
	const getSidebarWidth = () => {
		if (isMobile) {
			return 'w-64' // Always full width on mobile
		}
		if (menuState === 'collapsed' && isHovered) {
			return 'w-64' // Expand to full width when hovered
		}
		return menuState === 'collapsed' ? 'w-16' : 'w-64'
	}

	// Show text if menu is full OR if collapsed and hovered OR on mobile
	const showText = menuState === 'full' || (menuState === 'collapsed' && isHovered) || (isMobile && isMobileMenuOpen)

	// On mobile, show sidebar as overlay when isMobileMenuOpen is true
	if (isMobile) {
		return (
			<>
				{/* Mobile sidebar overlay */}
				<nav
					className={`
						fixed inset-y-0 left-0 z-[40] w-64 bg-white dark:bg-[#0F0F12]
						border-r border-gray-200 dark:border-[#1F1F23]
						transform transition-transform duration-300 ease-in-out
						${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}
					`}
				>
					<div className="h-full flex flex-col">
						{/* Header */}
						<div className="h-14 px-3 flex items-center">
							<Link href="/" className="flex items-center gap-3 w-full">
								<LogoIcon className="flex-shrink-0" />
								<span className="text-lg font-semibold hover:cursor-pointer text-gray-900 dark:text-white">
									Central Rebate System
								</span>
							</Link>
						</div>

						<div className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 scrollbar-none">
							<div className="space-y-6">
								{menuData.map((section) => (
									<div key={section.id}>
										<div className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider sidebar-section-label">
											{section.label}
										</div>
										<div className="space-y-1">
											{section.items?.map((item) => (
												<NavItem key={item.id} item={item} parentId={section.id} />
											))}
										</div>
									</div>
								))}
							</div>
						</div>
					</div>
				</nav>

				{/* Mobile overlay backdrop */}
				{isMobileMenuOpen && (
					<div className="fixed inset-0 bg-black opacity-50 z-30" onClick={() => setIsMobileMenuOpen(false)} />
				)}
			</>
		)
	}

	// Desktop sidebar
	return (
		<nav
			className={`
				fixed inset-y-0 left-0 z-[40] bg-sidebar
				border-r border-gray-200 dark:border-[#1F1F23] transition-all duration-300 ease-in-out
				${menuState === 'hidden' ? 'w-0 border-r-0' : getSidebarWidth()}
			`}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
			style={{
				overflow: menuState === 'hidden' ? 'hidden' : 'visible',
			}}
		>
			{menuState !== 'hidden' && (
				<div className="h-full flex flex-col relative">
					{/* Header */}
					<div className="h-14 px-3 flex items-center">
						{showText ? (
							<Link
								href="/"
								className="flex items-center gap-3 w-full"
							>
								<LogoIcon className="flex-shrink-0 hidden dark:block" />
								<LogoIcon className="flex-shrink-0 block dark:hidden" />
								<span className="text-lg font-semibold hover:cursor-pointer text-gray-900 dark:text-white transition-opacity duration-200">
									Central Rebate System
								</span>
							</Link>
						) : (
							<div className="flex justify-center w-full">
								<LogoIcon className="flex-shrink-0 hidden dark:block" />
								<LogoIcon className="flex-shrink-0 block dark:hidden" />
							</div>
						)}
					</div>

					<div
						className="flex-1 overflow-y-auto overflow-x-hidden py-4 px-2 scrollbar-none"
						style={{
							scrollbarWidth: 'none' /* Firefox */,
							msOverflowStyle: 'none' /* IE and Edge */,
						}}
					>
						<div className="space-y-6">
							{menuData.map((section) => (
								<div key={section.id}>
									{showText && (
										<div className="px-3 mb-2 text-xs font-semibold uppercase tracking-wider sidebar-section-label transition-opacity duration-200">
											{section.label}
										</div>
									)}
									<div className="space-y-1">
										{section.items?.map((item) => (
											<NavItem key={item.id} item={item} parentId={section.id} />
										))}
									</div>
								</div>
							))}
						</div>
					</div>
				</div>
			)}
		</nav>
	)
}
